/**
 * Frontend client for the Bulk / Batch Render API (server/modules/render).
 * Uses the shared workspace-scoped fetch helper (auth token + X-Workspace-Id
 * injected from localStorage).
 */
import { apiGet, apiPost, apiPatch, apiDelete } from '../shared/apiClient';

// ─────────────────────────── Types ──────────────────────────────────────────

export type RenderCompute = 'cloud' | 'onprem';
export type RenderBatchStatus = 'queued' | 'running' | 'done' | 'error' | 'canceled';
export type RenderJobStatus = 'queued' | 'claimed' | 'rendering' | 'done' | 'error' | 'canceled';

export interface RenderBatch {
  _id: string;
  compute: RenderCompute;
  resolution?: string;
  dpi?: number;
  options: Record<string, unknown>;
  status: RenderBatchStatus;
  totalJobs: number;
  doneJobs: number;
  createdAt: string;
  updatedAt: string;
}

export interface RenderJobOutput {
  key?: string;
  url?: string;
  angle?: string;
  metal?: string;
}

export interface RenderJob {
  _id: string;
  batchId: string;
  fileId: string;
  status: RenderJobStatus;
  progress: number;
  outputs: RenderJobOutput[];
  claimedByAgentId: string | null;
  claimedAt?: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Agent {
  _id: string;
  name: string;
  gpu?: string;
  agentToken?: string; // present only in the createAgent response — never listed again
  lastSeenAt?: string;
  status?: 'online' | 'offline'; // derived server-side in listAgents
  createdAt: string;
  updatedAt: string;
}

/** A single rendered image across the workspace (flattened from job outputs). */
export interface RenderOutput {
  id: string;
  url: string;
  name: string;
  angle?: string;
  metal?: string;
  batchId: string;
  fileId: string;
  ts: string;
}

export interface CreateBatchInput {
  fileIds: string[];
  compute: RenderCompute;
  resolution: string;
  dpi: number;
  options?: Record<string, unknown>;
}

// ─────────────────────────── Batches / Jobs ─────────────────────────────────

export const createBatch = (input: CreateBatchInput) =>
  apiPost<{ batch: RenderBatch; jobs: RenderJob[] }>('/render/batches', input);

export const listBatches = () => apiGet<RenderBatch[]>('/render/batches');

export const getBatch = (id: string) =>
  apiGet<{ batch: RenderBatch; jobs: RenderJob[] }>(`/render/batches/${id}`);

export const listJobs = (batchId: string) =>
  apiGet<RenderJob[]>(`/render/jobs?batchId=${encodeURIComponent(batchId)}`);

/** Every rendered image in the workspace, newest first (Drive "Renders" chip). */
export const listOutputs = () => apiGet<RenderOutput[]>('/render/outputs');

export const cancelBatch = (id: string) =>
  apiPost<RenderBatch>(`/render/batches/${id}/cancel`);

export const cancelJob = (id: string) =>
  apiPost<RenderJob>(`/render/jobs/${id}/cancel`);

// Browser-driven rendering: the viewer renders each job and reports back here.
export const updateJob = (
  id: string,
  patch: { progress?: number; outputs?: unknown[]; done?: boolean; error?: string },
) => apiPatch<RenderJob>(`/render/jobs/${id}`, patch);

export const addJobOutput = (id: string, body: { dataUrl: string; angle?: string; metal?: string }) =>
  apiPost<RenderJobOutput>(`/render/jobs/${id}/output`, body);

// ─────────────────────────── Agents ─────────────────────────────────────────

export const listAgents = () => apiGet<Agent[]>('/render/agents');

export const createAgent = (name: string, gpu: string) =>
  apiPost<Agent>('/render/agents', { name, gpu });

export const deleteAgent = (id: string) =>
  apiDelete<{ id: string }>(`/render/agents/${id}`);
